import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';

/**
 * Each nav entry now declares the permission required to see it. Admin
 * bypasses everything (per `useAuth().can`). The Dashboard is always
 * shown so a user with no perms still has a landing screen.
 */
const NAV: Array<{ path: string; label: string; perm?: string }> = [
  { path: '/',           label: 'Dashboard' },
  { path: '/orders',     label: 'Órdenes',         perm: 'orders.view' },
  { path: '/wholesale',  label: 'Mayorista',       perm: 'wholesale.view' },
  { path: '/cupet',      label: 'Combustible CUPET', perm: 'cupet.view' },
  { path: '/gas',        label: 'Gas / Balitas',     perm: 'gas.view' },
  { path: '/transfers',  label: 'Transferencias',  perm: 'transfers.view' },
  { path: '/auth-files', label: 'Autorizos',       perm: 'auth-files.view' },
  { path: '/stations',   label: 'Estaciones',      perm: 'stations.view' },
  { path: '/variants',   label: 'Combustibles',    perm: 'variants.view' },
  { path: '/users',      label: 'Usuarios',        perm: 'users.view' },
  { path: '/cron-runs',  label: 'Crons',           perm: 'cron-runs.view' },
  { path: '/audits',     label: 'Auditoría',       perm: 'audit.view' },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { me, logout, can } = useAuth();
  const loc = useLocation();
  const visible = NAV.filter((n) => !n.perm || can(n.perm));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: '100vh' }}>
      <aside style={{ borderRight: '1px solid var(--border)', padding: '24px 16px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 8px 24px' }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: 'var(--accent)', color: 'var(--accent-ink)', display: 'grid', placeItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L5 12c-1 1.5-1.5 3-1.5 4.5C3.5 20.5 7 23 12 23s8.5-2.5 8.5-6.5c0-1.5-.5-3-1.5-4.5L12 2z" />
            </svg>
          </div>
          <div style={{ lineHeight: 1 }}>
            <div className="display" style={{ fontWeight: 700, fontSize: 16 }}>tucupet</div>
            <div className="mono" style={{ fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.16em', marginTop: 2 }}>BACKOFFICE</div>
          </div>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {visible.map((n) => (
            <NavLink
              key={n.path}
              to={n.path}
              end={n.path === '/'}
              style={({ isActive }) => ({
                padding: '10px 12px',
                borderRadius: 10,
                background: isActive ? 'var(--surface-2)' : 'transparent',
                color: isActive ? 'var(--text)' : 'var(--text-2)',
                fontSize: 13,
                fontWeight: 500,
                textDecoration: 'none',
              })}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div style={{ padding: '16px 8px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 13 }}>{me?.firstName} {me?.lastName}</div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.1em' }}>{me?.role.toUpperCase()}</div>
          <button onClick={logout} style={{ background: 'transparent', border: '1px solid var(--border-2)', color: 'var(--text-2)', padding: '6px 10px', borderRadius: 8, fontSize: 12, marginTop: 4 }}>Cerrar sesión</button>
        </div>
      </aside>
      <main key={loc.pathname} className="fadeUp" style={{ padding: 32, overflow: 'auto' }}>{children}</main>
    </div>
  );
}
